import React, { useState } from 'react';
import {
  Box,
  Input,
  Text,
  VStack,
  HStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Modal } from 'src/components/atoms/Modal';
import { ModalBody, ModalFooter } from '@chakra-ui/react';
import { validateEmail } from 'src/utils/email';
import { useSendInviteMutation } from 'src/api/mutations';
import { useAppContext } from 'src/contexts/AppContext';
import { Icon } from 'src/components/atoms/Icon';

export const InviteColleaguesStep: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sendInviteMutation = useSendInviteMutation();
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName || '';
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (error) {
      setError('');
    }
  };

  const handleSendInvitation = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      setError(
        'This email address does not exist. Please make sure you have entered your email address correctly.',
      );
      return;
    }
    setIsLoading(true);
    try {
      sendInviteMutation.mutate({
        email,
        customer: customerId,
        role: 'user',
      });
      onOpen();
      setEmail('');
      setError('');
    } catch {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box w="100%">
      <VStack spacing={6} align="stretch">
        {/* Email Input Section */}
        <VStack spacing={4} align="stretch">
          <HStack spacing={4} align="flex-start">
            <Box flex="1">
              <Input
                value={email}
                onChange={handleEmailChange}
                placeholder="teammate's email"
                bg="surface.hover"
                border={'1px solid'}
                borderColor={error ? 'border.red' : 'transparent'}
                borderRadius="md"
                outline="none"
                boxShadow="none !important"
                px={4}
                py={3}
                fontSize="md"
                h="48px"
                isInvalid={!!error}
                _focus={{
                  borderColor: error ? 'border.red' : 'icon.green',
                  boxShadow: 'none !important',
                }}
                _hover={{
                  borderColor: error ? 'border.red' : 'icon.green',
                  boxShadow: 'none !important',
                }}
                _invalid={{
                  boxShadow: 'none !important',
                }}
              />
              {error && (
                <Text
                  color="border.red"
                  fontSize="sm"
                  mt={2}
                  fontFamily="marketingPage">
                  {error}
                </Text>
              )}
            </Box>

            <Button
              onClick={handleSendInvitation}
              isLoading={isLoading}
              loadingText="Sending..."
              bg={error ? 'gray.300' : 'icon.green'}
              color="white"
              px={4}
              py={3}
              borderRadius="md"
              fontSize="md"
              fontFamily="marketingPage"
              fontWeight="medium"
              w="180px"
              h="48px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              isDisabled={!!error}
              _hover={{
                bg: error ? 'gray.300' : 'icon.green',
                opacity: error ? 1 : 0.9,
              }}
              _active={{
                bg: error ? 'gray.300' : 'icon.green',
                opacity: error ? 1 : 0.8,
              }}
              _disabled={{
                bg: 'gray.300',
                color: 'white',
                cursor: 'not-allowed',
                opacity: 1,
              }}
              buttonName="send-invitation">
              Send invitation
            </Button>
          </HStack>
          <HStack spacing={1} align="flex-start">
            <Box>
              <Icon iconName="info-filled" boxSize={4} color="icon.yellow" />
            </Box>
            <VStack spacing={0} align="flex-start">
              <Text fontSize={16} fontWeight="medium" color="text.title">
                Your plan includes 5 free teammate seats
              </Text>
              <Text fontSize={14} fontWeight="medium" color="text.darkGray">
                You can upgrade your plan later in Settings.{' '}
              </Text>
            </VStack>
          </HStack>
        </VStack>

        {/* Success Modal */}
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          header=""
          maxWidth="588px"
          closeOnOutsideClick={false}>
          <ModalBody pt={9} px={9}>
            <VStack spacing={4} align="stretch">
              <Text
                fontSize="40px"
                fontWeight="bold"
                color="text.light.highContrast"
                textAlign="center"
                fontFamily="marketingPage">
                Invitation successfully sent
              </Text>
              <Text
                color="text.muted"
                fontSize="md"
                textAlign="center"
                fontFamily="marketingPage">
                Your colleague has received an invitation by email. To join,
                they need to confirm it in their email.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter px={9} pb={9}>
            <Button
              onClick={onClose}
              bg="icon.green"
              h="48px"
              color="white"
              px={3}
              py={4}
              borderRadius="md"
              fontSize="md"
              fontWeight="medium"
              fontFamily="marketingPage"
              lineHeight={'24px'}
              w="100%"
              _hover={{
                bg: 'icon.green',
                opacity: 0.9,
              }}
              _active={{
                bg: 'icon.green',
                opacity: 0.8,
              }}
              buttonName="modal-ok">
              Ok
            </Button>
          </ModalFooter>
        </Modal>
      </VStack>
    </Box>
  );
};
